//Login.js
import React, { useState, useEffect } from 'react';
import Modal from 'react-bootstrap/Modal';
import 'bootstrap/dist/css/bootstrap.min.css';
import { BeatLoader } from 'react-spinners';
import {QRCodeSVG} from 'qrcode.react';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { api } from '../../api';
import Header from '../header/header';

// notification
import { notifyError, notifySuccess, Notification } from '../Notification';

// set up amplify config
import { Amplify } from 'aws-amplify';
import { signIn, confirmSignIn, setUpTOTP, verifyTOTPSetup, updateMFAPreference, fetchAuthSession } from 'aws-amplify/auth';

import awsconfig from '../../aws-exports';

import { useNavigate } from 'react-router-dom';

import './auth.css';

import SignUp from './SignUp';
import ForgotPassword from './ForgotPassword';

Amplify.configure(awsconfig);

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [code, setCode] = useState('');
  const [showSignup, setShowSignup] = useState(false);
  const [showResetPass, setShowResetPass] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isMFA, setIsMFA] = useState(false);
  const [MFAconfirm, setMFAconfirm] = useState(false);
  const [loadingVerificationCode, setLoadingVerificationcode] = useState(false);
  const [loadingSignIn, setLoadingSignIn] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [isTOTPsetUpRequired, setIsTOTPsetUpRequired] = useState(false);
  const [TOTPsetupCode, setTOTPsetupCode] = useState('');
  const [activeTab, setActiveTab] = useState('login');

  const navigate = useNavigate();

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
  }

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
  }
 
  // Sign up modal
  const handleCloseSignup = () => {
    setShowSignup(false);
  } 

  const handleShowSignup = () => {   
    setActiveTab('signup');
  }

  // Forgot Password modal
  const handleCloseResetPass = () => {
    setShowResetPass(false);    
  } 

  const handleShowResetPass = () => {   
    setShowResetPass(true);    
  }

  // retrieve device_id from URL and store it in localStorage
  function saveDeviceIdFromUrl() {
    try {
      const params = new URLSearchParams(window.location.search);
      console.log('URL params:', params.toString());
      const deviceId = params.get('device_id');
  
      // Remove old device_id if it exists
      localStorage.removeItem('device_id');
  
      // Store new device_id if found
      if (deviceId) {
        localStorage.setItem('device_id', deviceId);
      }
  
      return deviceId;
    } catch (error) {
      console.error('Error handling device_id from URL:', error);
      return null;
    }
  }

  // Process pending invitation function
  const processPendingInvitation = async (token, userEmail) => {
    try {
      console.log('🔄 Processing pending invitation for:', userEmail);
      
      // Get the current auth session to extract the real email
      const authSession = await fetchAuthSession();
      
      // Extract email from ID token (which contains user attributes)
      let actualEmail = userEmail; // fallback
      
      if (authSession?.tokens?.idToken) {
        try {
          const idTokenPayload = authSession.tokens.idToken.payload;
          actualEmail = idTokenPayload.email || idTokenPayload.username || userEmail;
          console.log('📧 Extracted email from ID token:', actualEmail);
        } catch (error) {
          console.log('⚠️ Could not extract email from ID token, using provided email');
        }
      }
      
      // Use the public route - no auth headers needed!
      const response = await api.post('/invite/accept-public', {
        token: token,
        userEmail: actualEmail
      });
      
      if (response.data.success) {
        notifySuccess(`Welcome to ${response.data.data.businessName}! Your invitation has been accepted.`);
        localStorage.removeItem('pendingInviteToken');
      }
      
    } catch (error) {
      console.error('❌ Error processing pending invitation:', error);
      
      if (error.response?.status === 400) {
        const errorMsg = error.response.data.message;
        notifyError(errorMsg || 'Invalid invitation request.');
      } else {
        notifyError('Failed to process invitation. Please try again.');
      }
      
      localStorage.removeItem('pendingInviteToken');
    }
  };

  // Centralized success handler
  const handleSuccessfulLogin = async (userEmail) => {
    try {
      // Get the full auth session
      const authSession = await fetchAuthSession();
      
      if (authSession?.tokens?.idToken && authSession?.tokens?.accessToken) {
        // Extract user information from the ID token (for user details)
        const idTokenPayload = authSession.tokens.idToken.payload;
        
        // Store user information
        const userInfo = {
          email: idTokenPayload.email || userEmail,
          firstName: idTokenPayload.given_name,
          lastName: idTokenPayload.family_name,
          role: idTokenPayload['custom:role'], // if you store role in Cognito
          sub: idTokenPayload.sub // Cognito user ID
        };
        
        // Store user email (backward compatibility)
        localStorage.setItem('user', userInfo.email);
        
        // Store full user info for future use
        localStorage.setItem('userInfo', JSON.stringify(userInfo));
        
        console.log('✅ User info stored:', userInfo);
        console.log('✅ Access token available for API calls');
      }
      
      // Check for pending invitation with a delay to ensure auth is established
      const pendingInvite = localStorage.getItem('pendingInviteToken');
      if (pendingInvite) {
        console.log('📨 Found pending invitation, processing...');
        setTimeout(async () => {
          await processPendingInvitation(pendingInvite, userEmail);
        }, 1500);
      }
            
    } catch (error) {
      console.error('❌ Error in handleSuccessfulLogin:', error);
      // Still proceed with basic email storage
      localStorage.setItem('user', userEmail);
      navigate('/');
    }
  };
 
  // Check if user is already authenticated
  useEffect(() => {
   // retieve device_id from URL and store it in localStorage
    const deviceId = saveDeviceIdFromUrl();

    const checkAuth = async () => {
      setCheckingAuth(true);
      
      try {
        const authSession = await fetchAuthSession();
        
        if (authSession && authSession.tokens && authSession.tokens.accessToken) {
          console.log('User is already authenticated');
          if (deviceId) {
            // If deviceId is present, navigate to DeviceSetup
            navigate('/DeviceSetup?device_id=' + deviceId);
          }
          const userEmail = authSession.tokens.idToken?.payload?.email;
          if (userEmail && !localStorage.getItem('user')) {
            localStorage.setItem('user', userEmail);
          }
          
          // Check for pending invitation
          const pendingInvite = localStorage.getItem('pendingInviteToken');
          if (pendingInvite) {
            await processPendingInvitation(pendingInvite, userEmail);
            localStorage.removeItem('pendingInviteToken');
          }
          if (deviceId) {
            // If deviceId is present, navigate to DeviceSetup
            navigate('/DeviceSetup?device_id=' + deviceId);
          }
          else{
            navigate('/');
          }

        } else {
          console.log('No valid tokens found');
          localStorage.removeItem('user');
          
          // Pre-fill email from URL params if coming from invitation
          const urlParams = new URLSearchParams(window.location.search);
          const inviteEmail = urlParams.get('email');
          if (inviteEmail) {
            setEmail(inviteEmail);
            setActiveTab('signup'); // Show signup tab for invitations
          }
        }
      } catch (error) {
        console.log('Not currently authenticated:', error.message);
        localStorage.removeItem('user');
        
        // Still check for URL params even if not authenticated
        const urlParams = new URLSearchParams(window.location.search);
        const inviteEmail = urlParams.get('email');
        if (inviteEmail) {
          setEmail(inviteEmail);
          setActiveTab('signup'); // Show signup tab for invitations
        }
      } finally {
        setCheckingAuth(false);
      }
    };
    
    checkAuth();
  }, [navigate]);

  const confirmHandleLogin = async() => {
   // retieve device_id from URL and store it in localStorage
   const deviceId = saveDeviceIdFromUrl();
    try {
      const responseConfirmSignIn = await confirmSignIn({
        challengeResponse: code
      });
      console.log('responseConfirmSignIn:', responseConfirmSignIn);

      setLoadingSignIn(true);
      setMFAconfirm(true);
      setLoadingSignIn(false);
      setMFAconfirm(true);
      // store user email to local storage
      localStorage.setItem('user', email);
      await handleSuccessfulLogin(email);

      if (deviceId) {
        // If deviceId is present, navigate to DeviceSetup
        navigate('/DeviceSetup?device_id=' + deviceId);
      }
      else {
        navigate('/');
      }
      
      // Use centralized success handler

    } catch (error) {
      console.error('Error confirming sign in:', error);

      if (error.message === 'Invalid session for the user, session is expired.') {
        let errorMsg = 'session is expired, please login again.';
        console.log(errorMsg);
        notifyError(errorMsg);
        setIsMFA(false);
      }
      else if (error.message === 'Invalid code received for user') 
      {
        let errorMsg = 'Invalid code received. Please enter the correct code.';
        console.log(errorMsg);
        notifyError(errorMsg);
       
        setLoadingSignIn(false);   
      }
      else {
        let errorMsg = 'Error confirming sign in. Please try again.';
        console.log(errorMsg);
        notifyError(errorMsg);
        setIsMFA(false);
        setLoadingSignIn(false);
      }
    }
  }

  // handle enter key press for confirm login button
  const handleEnterKeyPress = (event) => {
    if (event.key === 'Enter') {
      confirmHandleLogin();
    }
  };

  // handle enter key press for login button
  const handleEnterKeyPressLogin = (event) => {
    if (event.key === 'Enter') {
      handleLogin();
    }
  };

  const handleCancel = () => {
    setIsMFA(false);
    setEmail('');
    setIsTOTPsetUpRequired(false);
    setTOTPsetupCode('');
    setPassword('');
    setLoadingVerificationcode(false);
    setLoadingSignIn(false);
    setActiveTab('login');
    localStorage.removeItem('user');
  }

  const verifyTOTP = async () => {
    // retieve device_id from URL and store it in localStorage
    const deviceId = saveDeviceIdFromUrl();
    try {
      setLoadingSignIn(true);
      await confirmSignIn({
        challengeResponse: code
      });
     
      // set TOTP as the preferred MFA method.
      await updateMFAPreference({ totp: 'PREFERRED' });     
      setMFAconfirm(true);
      setLoadingSignIn(false);
      
      // Use centralized success handler
      await handleSuccessfulLogin(email);
      if (deviceId) {
        // If deviceId is present, navigate to DeviceSetup
        navigate('/DeviceSetup?device_id=' + deviceId);
      }
      // If deviceId is not present, navigate to Admin
      else {
        navigate('/');
      }

    } catch (error) {
      console.error('Error verifying TOTP:', error);
      setLoadingSignIn(false);

      if (error.message === 'Invalid session for the user, session is expired.') {
        let errorMsg = 'session is expired, please login again.';
        notifyError(errorMsg);
        console.log(errorMsg);
        setIsMFA(false);
      }
      else {
        let errorMsg = 'Error verifying TOTP. Please try again.';
        notifyError(errorMsg);
        setIsMFA(false);
        handleCancel();
      } 
    }
  }

  const handleTOTPSetup = (secretCode) => {       
    try {
      setTOTPsetupCode(secretCode);
      setIsTOTPsetUpRequired(true);
    } 
    catch (error) {
      console.error('Error setting up TOTP:', error);
      console.log('Error setting up TOTP:', error);
    }
  };

  const handleLogin = async () => {
    setSubmitted(true);
    try{
      setLoadingVerificationcode(true);
      // clear local storage
      localStorage.removeItem('user');
      const { isSignedIn, nextStep } = await signIn({ username: email, password });
      console.log('isSignedIn:', isSignedIn);
      console.log('nextStep:', nextStep);
      setLoadingVerificationcode(false);

      if (nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
        setIsMFA(true);
      }

      // check if first time TOTP setup required
      if (nextStep.signInStep === 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP') {
        setIsTOTPsetUpRequired(true);
        const secretcode = nextStep.totpSetupDetails?.sharedSecret;
        handleTOTPSetup(secretcode); // set up TOTP (Authenticator app MFA)
      }

      if (nextStep.signInStep === 'CONFIRM_SIGN_UP') {
        let errorMsg = 'You can not login because sign up is pending. Please Sign up first.';
        console.log(errorMsg);
        notifyError(errorMsg);
      }

      // Handle case where login is complete (no MFA required)
      if (isSignedIn) {
        await handleSuccessfulLogin(email);
        navigate('/');
      }

    } catch (error) {
      console.log(error.message)
      setLoadingVerificationcode(false);
      if (error.message === 'User is not confirmed.') {
        let errorMsg = 'You can not login because user verification is pending.';
        notifyError(errorMsg);
        console.log(errorMsg);     
      }
      else if (error.message === 'User does not exist.') {
        let errorMsg = `${email} does not exist. Please Sign up first.`;
        notifyError(errorMsg);
        console.log(errorMsg);
      }
      else if (error.message === 'Custom auth lambda trigger is not configured for the user pool.') {
        let errorMsg = "Password can't be blank";
        notifyError(errorMsg);
        console.log(errorMsg);     
      }
      else if (error.message === 'Invalid session for the user, session can only be used once.') {
        let errorMsg = 'Invalid session. Try again later';
        notifyError(errorMsg);
        setIsMFA(false);
        setIsTOTPsetUpRequired(false);
      }
      else if (error.message === 'There is already a signed in user.') {
        notifyError('There is already a signed in user. Please sign out first.');
        setIsMFA(true);
      }
      else {
        notifyError(error.message);
        console.log(error.message);
        setIsTOTPsetUpRequired(false);
        setIsMFA(false);
      }
    }
  }  

  const switchTab = (tab) => {
    setActiveTab(tab);
  };

  // Show loading screen while checking authentication
  if (checkingAuth) {
    return (
      <div className="auth-loading-container">
        <BeatLoader size={10} color="#667eea"/>
      </div>
    );
  }

  const urlParams = new URLSearchParams(window.location.search);
  const inviteEmail = urlParams.get('email');
  const isInvitation = !!inviteEmail;

  return (
  <>
  <Header hideLoginButton={true} />
    <div className='auth-page-container'>
      <div className='auth-container'>
        {/* Brand Header */}
        <div className='brand-header'>
          <div className='brand-logo'>fdbk</div>
          <div className='brand-subtitle'>Customer feedback that evolves</div>
        </div>

        {/* Tab Navigation */}
        <div className='auth-tabs'>
          <button 
            className={`auth-tab ${activeTab === 'login' ? 'active' : ''}`}
            onClick={() => switchTab('login')}
          >
            Sign In
          </button>
          <button 
            className={`auth-tab ${activeTab === 'signup' ? 'active' : ''}`}
            onClick={() => switchTab('signup')}
          >
            Sign Up
          </button>
        </div>

        {/* Login Section */}
        {activeTab === 'login' && !isMFA && !isTOTPsetUpRequired && (
          <div className='auth-form-section'>
            <div className='auth-header'>
              <h1 className='auth-title'>Welcome back</h1>
              <p className='auth-subtitle'>Sign in to your account to continue</p>
            </div>

            <form onSubmit={(e) => { e.preventDefault(); handleLogin(); }}>
              <div className='form-group'>
                <label className='form-label' htmlFor="login-email">Email address</label>
                <input 
                  id="login-email"
                  className={`form-input ${isInvitation ? 'readonly' : ''}`}
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={handleEmailChange}
                  readOnly={isInvitation}
                  onKeyUp={handleEnterKeyPressLogin}
                  required
                />
              </div>

              <div className='form-group'>
                <label className='form-label' htmlFor="login-password">Password</label>
                <div className="password-input-wrapper">
                  <input 
                    id="login-password"
                    className='form-input'
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={password}
                    onChange={handlePasswordChange}
                    onKeyUp={handleEnterKeyPressLogin}
                    required
                  />
                  <button 
                    type="button" 
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    <FontAwesomeIcon icon={showPassword ? faEye : faEyeSlash} />
                  </button>
                </div>
              </div>

              <button 
                type="submit" 
                className='submit-btn' 
                disabled={loadingVerificationCode}
              >
                {loadingVerificationCode ? (
                  <>
                    <div className="loading-spinner"></div>
                    Processing...
                  </>
                ) : 'Sign In'}
              </button>
            </form>

            <div className='auth-links'>
              <button 
                type="button"
                className='auth-link-btn' 
                onClick={handleShowResetPass}
              >
                Forgot password?
              </button>
            </div>
          </div>
        )}

        {/* Signup Section */}
        {activeTab === 'signup' && (
          <div className='auth-form-section'>
            <SignUp 
              onClose={() => setActiveTab('login')} 
              prefilledEmail={inviteEmail}
              onSignUpComplete={() => setActiveTab('login')}
            />
          </div>
        )}

        {/* TOTP Setup Section */}
        {isTOTPsetUpRequired && TOTPsetupCode && (
          <div className='auth-form-section'>
            <div className='mfa-section'>
              <div className='mfa-title'>Set up Two-Factor Authentication</div>
              <div className='mfa-description'>
                Scan the QR code below with your authenticator app (Google Authenticator, Authy, etc.)
              </div>
              
              <div className='qr-container'>
                <QRCodeSVG 
                  value={`otpauth://totp/fdbk:${email}?secret=${TOTPsetupCode}&issuer=FDBK`} 
                  size={200}
                />
              </div>

              <div className='form-group'>
                <label className='form-label' htmlFor="totp-code">Enter verification code</label>
                <input
                  id="totp-code"
                  type="text"
                  className='form-input verification-input'
                  placeholder="000000"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  maxLength="6"
                />
              </div>

              <button 
                className='submit-btn' 
                onClick={verifyTOTP}
                disabled={loadingSignIn}
              >
                {loadingSignIn ? (
                  <>
                    <div className="loading-spinner"></div>
                    Verifying...
                  </>
                ) : 'Verify & Continue'}
              </button>

              <button className='cancel-btn' onClick={handleCancel}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* MFA Verification Section */}
        {isMFA && (
          <div className='auth-form-section'>
            <div className='mfa-section'>
              <div className='mfa-title'>Two-Factor Authentication</div>
              <div className='mfa-description'>
                Please enter the 6-digit code from your authenticator app
              </div>

              <div className='form-group'>
                <input
                  type="text"
                  className='form-input verification-input'
                  placeholder="000000"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  onKeyUp={handleEnterKeyPress}
                  maxLength="6"
                />
              </div>

              <button 
                className='submit-btn' 
                onClick={confirmHandleLogin}
                disabled={loadingSignIn}
              >
                {loadingSignIn ? (
                  <>
                    <div className="loading-spinner"></div>
                    Verifying...
                  </>
                ) : 'Verify Code'}
              </button>

              <button className='cancel-btn' onClick={handleCancel}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Forgot Password Modal */}
      <Modal 
        size='md' 
        centered 
        show={showResetPass} 
        onHide={handleCloseResetPass}
        dialogClassName="forgot-password-modal"
      >
        <Modal.Header className="forgot-password-modal-header">
          <button 
            className="modal-btn-close" 
            onClick={handleCloseResetPass}
            aria-label="Close"
          >
            ×
          </button>
        </Modal.Header>
        <Modal.Body className="forgot-password-modal-body">
          <ForgotPassword username={email} onClose={handleCloseResetPass} />
        </Modal.Body>
      </Modal>
    </div>
  </>
  );
};

export default LoginPage;